import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

export default function Verify() {
  const { t } = useTranslation("verify");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { verify } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const phone = (location.state as { phone?: string })?.phone || "";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!phone) {
      setError(t("verify.error.expired"));
      setIsLoading(false);
      return;
    }

    const cleanCode = code.trim();
    if (!cleanCode || cleanCode.length < 4) {
      setError(t("verify.error.invalidOtp"));
      setIsLoading(false);
      return;
    }

    const result = await verify(phone, cleanCode);
    setIsLoading(false);

    if (result.success) {
      navigate("/dashboard");
    } else {
      setError(result.error || t("verify.error.wrongOtp"));
    }
  };

  if (!phone) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
        <div className="text-center max-w-md">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-accent-100 flex items-center justify-center">
            <i className="ri-time-line text-accent-600 text-2xl"></i>
          </div>
          <h1 className="font-heading text-2xl text-foreground-950 mb-2">
            {t("verify.expired.title")}
          </h1>
          <p className="text-foreground-500 text-sm mb-6">
            {t("verify.expired.message")}
          </p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 bg-primary-500 text-white text-sm font-medium px-6 py-3 rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-sm"></i>
            {t("verify.expired.backToLogin")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-accent-100 flex items-center justify-center">
            <i className="ri-shield-check-line text-accent-600 text-2xl"></i>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-2">
            {t("verify.title")}
          </h1>
          <p className="text-foreground-500 text-sm">
            {t("verify.subtitle")}{" "}
            <span className="text-foreground-700 font-medium">{phone}</span>
          </p>
        </div>

        <div className="bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/50">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-2">
                {t("verify.codeLabel")}
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                  <i className="ri-lock-password-line text-foreground-400 text-sm"></i>
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder={t("verify.codePlaceholder")}
                  maxLength={6}
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-background-100 border border-background-200 text-foreground-950 text-sm outline-none focus:border-primary-400 transition-colors placeholder:text-foreground-400 tracking-widest"
                  required
                />
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 rounded-lg px-3 py-2.5">
                <i className="ri-error-warning-line text-sm"></i>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary-500 text-white text-sm font-medium py-3 rounded-xl hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  {t("verify.verifying")}
                </>
              ) : (
                <>
                  {t("verify.submit")}
                  <i className="ri-arrow-right-line text-sm"></i>
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-foreground-500 text-sm mt-6">
          {t("verify.wrongNumber")}{" "}
          <Link to="/login" className="text-primary-600 hover:text-primary-700 transition-colors underline">
            {t("verify.goBack")}
          </Link>
        </p>
      </div>
    </div>
  );
}